import classnames from 'classnames'
import { sortBy } from 'lodash'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'
import { Link } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import ConfirmDialog from '../../components/basics/ConfirmDialog/ConfirmDialog'
import Menu from '../../components/basics/Menu/Menu'
import GroupSlug from '../../components/slugs/GroupSlug/GroupSlug'
import Slugs from '../../components/slugs/Slugs/Slugs'
import UserAvatar from '../../components/UserAvatar/UserAvatar'
import Button from '../../components/basics/Button/Button'
import Checkbox from '../../components/basics/Checkbox/Checkbox'
import TextInput from '../../components/basics/TextInput/TextInput'
import OverlayChildPage from '../../components/OverlayChildPage/OverlayChildPage'
import Pagination from '../../components/Pagination/Pagination'
import UserSlugs from '../../components/slugs/UserSlugs'
import { Group } from '../../models/Group'
import { User } from '../../models/User'
import { userCanCreateSomeUser } from '../../state/calculations/permissions/userCan'
import useToggleUsersActive from '../../state/mutations/useToggleUsersActive'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import useUserFilter from '../../state/queries/useUserFilter'
import useDialog from '../../utilities/useDialog'
import useItemSelection from '../../utilities/useItemSelection'
import { ItemSelectorInit } from '../../utilities/useItemSelector'

import styles from './ManageUsersPage.module.scss'

/**********************************************************************
 * ConfirmToggleUsersActiveDialog
 **********************************************************************/
const ConfirmToggleUsersActiveDialog = React.memo(function ConfirmToggleUsersActiveDialog({
  userUuids,
  isActive,
  close,
}: {
  userUuids: string[]
  isActive: boolean
  close: (result: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <ConfirmDialog close={close} yesLabel={t('generic cta.yes')} noLabel={t('generic cta.cancel')}>
      {isActive && <p>{t('manage user.should {count} users be activated', { count: userUuids.length })}</p>}
      {!isActive && <p>{t('manage user.should {count} users be deactivated', { count: userUuids.length })}</p>}
      <UserSlugs uuids={userUuids} max={20} />
    </ConfirmDialog>
  )
})

/**********************************************************************
 * ManagerUsersPageUserGroups
 *
 * List of group slugs in the users table.
 **********************************************************************/
const renderGroupMembershipSlugs = (group: Group) => <GroupSlug key={group.uuid} group={group} />

const ManagerUsersPageUserGroups = React.memo(({ user }: { user: User }) => {
  const groupMemberships = useMemo(() => {
    const g: { [uuid: string]: Group & { isMember?: boolean; isGroupManager?: boolean } } = {}
    for (const group of user.groups || []) {
      g[group.uuid] = g[group.uuid] || { ...group }
      g[group.uuid].isMember = true
    }
    for (const group of user.managedGroups || []) {
      g[group.uuid] = g[group.uuid] || { ...group }
      g[group.uuid].isGroupManager = true
    }
    return sortBy(
      Array.from(Object.values(g)),
      (g) => g.isGroupManager,
      (g) => g.name
    )
  }, [user])

  return (
    <Slugs
      items={groupMemberships}
      total={groupMemberships.length}
      max={5}
      render={renderGroupMembershipSlugs}
      className={styles.ManageUsersPageUserGroups}
    />
  )
})

/**********************************************************************
 * ManagerUsersPage
 **********************************************************************/
const userSelectionInit: ItemSelectorInit<User, User> = {
  createItem: (base) => base,
  limitToOptions: true,
}

export default React.memo(function ManageUsersPage() {
  const { t } = useTranslation()
  const routes = useRoutes()
  const showDialog = useDialog()
  const location = useLocation()

  const { user } = useSession()

  const userFilter = useUserFilter()

  // search
  const updateSearch = useDebouncedCallback(userFilter.setSearch, 300)
  const [search, setSearch] = useState(userFilter.search)
  const handleSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value)
      updateSearch(e.currentTarget.value)
    },
    [setSearch, updateSearch]
  )

  // selection
  const userSelection = useItemSelection(userSelectionInit, userFilter.users)

  // actions
  const userActionItems: React.ReactNode[] = []

  // assign learning item
  const handleAddLearningAssignments = useCallback(
    () =>
      routes.addLearningItemsToUsers.withQuery(location.search).goto(
        {
          uuids: userSelection.items.map(({ uuid }) => uuid).join(','),
        },
        { preventScrollReset: true }
      ),
    [routes, userSelection.items, location.search]
  )
  if (user?.permissions.user.assign_learning_item) {
    userActionItems.push(
      <li key="add-learning-assignments">
        <Button menuItem onClick={handleAddLearningAssignments}>
          {t('manage learning.cta assign learning item')}
        </Button>
      </li>
    )
  }

  // add to group
  const handleAddToGroup = useCallback(
    () =>
      routes.AddGroupMembershipsToUsers.withQuery(location.search).goto(
        {
          uuids: userSelection.items.map(({ uuid }) => uuid).join(','),
        },
        { preventScrollReset: true }
      ),
    [routes, userSelection.items]
  )
  if (user?.permissions.group.add_member) {
    userActionItems.push(
      <li key="add-to-group">
        <Button menuItem onClick={handleAddToGroup}>
          {t('manage group.add to group')}
        </Button>
      </li>
    )
  }

  // remove from group
  const handleRemoveFromGroups = useCallback(
    () =>
      routes.removeGroupMembershipsFromUsers.withQuery(location.search).goto(
        {
          uuids: userSelection.items.map(({ uuid }) => uuid).join(','),
        },
        { preventScrollReset: true }
      ),
    [routes, userSelection.items]
  )
  if (user?.permissions.group.remove_member) {
    userActionItems.push(
      <li key="remove-from-group">
        <Button menuItem onClick={handleRemoveFromGroups}>
          {t('manage group.remove from group')}
        </Button>
      </li>
    )
  }

  // activate / deactivate
  const toggleUsersActive = useToggleUsersActive()
  const handleToggleUsersActive = useCallback(
    async (isActive: boolean) => {
      const confirmed = await showDialog(
        (close) => (
          <ConfirmToggleUsersActiveDialog
            close={close}
            userUuids={userSelection.items.map(({ uuid }) => uuid)}
            isActive={isActive}
          />
        ),
        () => false
      )
      if (confirmed) {
        toggleUsersActive.mutate({ userUuids: userSelection.items.map(({ uuid }) => uuid), isActive })
      }
    },
    [toggleUsersActive, userSelection.items]
  )
  const handleActivateUsers = useCallback(() => handleToggleUsersActive(true), [handleToggleUsersActive])
  const handleDeactivateUsers = useCallback(() => handleToggleUsersActive(false), [handleToggleUsersActive])
  if (user?.permissions.user.change_status) {
    userActionItems.push(
      <React.Fragment key="activate-deactivate">
        <li>
          <Button menuItem onClick={handleActivateUsers}>
            {t('manage user.cta activate user')}
          </Button>
        </li>
        <li>
          <Button menuItem onClick={handleDeactivateUsers}>
            {t('manage user.cta deactivate user')}
          </Button>
        </li>
      </React.Fragment>
    )
  }

  return (
    <article className={styles.ManageUsersPage}>
      <header>
        <h1>{t('manage users')}</h1>
      </header>

      <main>
        <div className={styles.searchbar}>
          <TextInput
            name="search"
            label={t('search users')}
            type="text"
            autoComplete="off"
            omitErrorDisplay
            value={search || ''}
            onChange={handleSearchChanged}
          />
          {userActionItems.length > 0 && (
            <div className={styles.actions}>
              <Menu small label={t('generic cta.select action')} disabled={userSelection.items.length === 0}>
                {userActionItems}
              </Menu>
            </div>
          )}
          {userCanCreateSomeUser(user) && (
            <Button small filled component={Link} to={routes.createUser.url({})}>
              {t('manage user.create user')}
            </Button>
          )}
        </div>

        <div className={classnames(styles.usertable, { skeleton: userFilter.result.isPlaceholderData })}>
          <table>
            <thead>
              <tr>
                <th className={styles.selectColumn}>
                  {userActionItems.length > 0 && (
                    <Checkbox
                      omitErrorDisplay
                      onChange={userSelection.handleSelectAll}
                      checked={userSelection.items.length === userFilter.users.length}
                    />
                  )}
                </th>
                <th className={styles.nameColumn}>{t('name')}</th>
                <th className={styles.groupColumn}>{t('groups')}</th>
                <th className={styles.statusColumn}>{t('user.user status')}</th>
                <th className={styles.joinedColumn}>{t('user.joined at')}</th>
              </tr>
            </thead>
            <tbody>
              {userFilter.users.map((user) => (
                <tr key={user.uuid} className={classnames({ [styles.inactive]: user.status !== 'active' })}>
                  <td className={styles.selectColumn}>
                    {}
                    {userActionItems.length > 0 ? (
                      <Checkbox
                        omitErrorDisplay
                        value={user.uuid}
                        onChange={userSelection.handleSelected}
                        checked={!!userSelection.ids[user.uuid]}
                      >
                        <UserAvatar user={user} />
                      </Checkbox>
                    ) : (
                      <UserAvatar user={user} />
                    )}
                  </td>
                  <td className={styles.nameColumn}>
                    <Link to={routes.manageUser.url({ uuid: user.uuid })}>
                      {user.firstname} {user.lastname}
                    </Link>
                  </td>
                  <td className={styles.groupColumn}>
                    <ManagerUsersPageUserGroups user={user} />
                  </td>
                  <td className={styles.statusColumn}>
                    <span>{t(`user.user_status.${user.status}`)}</span>
                  </td>
                  <td className={styles.joinedColumn}>
                    <span>{t('user.user joined at date', { date: user.createdAt })}</span>
                  </td>
                </tr>
              ))}
              {!userFilter.users.length && (
                <tr>
                  <td colSpan={4} className={styles.empty}>
                    {t('no users found')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <Pagination
            page={userFilter.page}
            pageCount={userFilter.pageCount}
            pageSize={userFilter.pageSize}
            itemCount={userFilter.itemCount}
            onPageChanged={userFilter.setPage}
          />
        </div>
      </main>

      <OverlayChildPage />
    </article>
  )
})

export function ManageUsersPageBreadcrumb() {
  const { t } = useTranslation()
  const routes = useRoutes()
  return <Link to={routes.manageUsers.url({})}>{t('manage users')}</Link>
}
